import { AnimatePresence, motion } from 'framer-motion'
import { X } from 'lucide-react'
import PropTypes from 'prop-types'
import { useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { markSeen } from '../../api/whatsNewApi'
import { useTheme } from '../../theme/hooks'

function WhatsNewModal({ post, isOpen, onClose }) {
  const { theme } = useTheme()
  const navigate = useNavigate()
  const sealedRef = useRef(false)

  const seal = async () => {
    if (sealedRef.current || !post?.id) return
    sealedRef.current = true
    try {
      await markSeen(post.id)
    } catch {
      // seen is idempotent + non-critical; ignore failures
    }
    window.dispatchEvent(new CustomEvent('whatsnew-seen'))
  }

  const handleClose = async () => {
    await seal()
    onClose()
  }

  const handleDiscover = async () => {
    await seal()
    onClose()
    navigate(`/whats-new/${post.slug}`)
  }

  const handleSeeAll = async () => {
    await seal()
    onClose()
    window.dispatchEvent(new CustomEvent('whatsnew-open-panel'))
  }

  if (!isOpen || !post) return null

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[999999] flex items-center justify-center p-4"
        style={{ backgroundColor: theme.modal.overlay }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-lg"
          style={{
            backgroundColor: theme.modal.bg,
            boxShadow: theme.modal.shadow,
          }}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        >
          <div className="relative flex-shrink-0">
            {post.cover_image_url && (
              <img
                src={post.cover_image_url}
                alt=""
                className="h-44 w-full object-cover"
              />
            )}
            <button
              onClick={handleClose}
              aria-label="Fermer"
              className="absolute right-2 top-2 rounded-full p-1"
              style={{
                backgroundColor: theme.modal.bg,
                color: theme.text.tertiary,
              }}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto p-5">
            <h2
              className="text-xl font-semibold"
              style={{ color: theme.text.primary }}
            >
              {post.title}
            </h2>
            <p
              className="whitespace-pre-line text-sm leading-relaxed"
              style={{ color: theme.text.secondary }}
            >
              {post.summary}
            </p>
          </div>

          <div
            className="flex flex-shrink-0 items-center justify-between gap-3 p-5 pt-4"
            style={{ borderTop: `1px solid ${theme.border.primary}` }}
          >
            <button
              onClick={handleDiscover}
              data-cy="whatsnew-modal-discover"
              className="rounded px-4 py-2 text-sm font-medium transition-colors"
              style={{
                backgroundColor: theme.button.primary.bg,
                color: theme.button.primary.text,
              }}
              onMouseEnter={e =>
                (e.currentTarget.style.backgroundColor =
                  theme.button.primary.bgHover)
              }
              onMouseLeave={e =>
                (e.currentTarget.style.backgroundColor =
                  theme.button.primary.bg)
              }
            >
              Découvrir
            </button>
            <button
              onClick={handleSeeAll}
              className="text-sm underline"
              style={{ color: theme.text.tertiary }}
            >
              Voir toutes les nouveautés
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  )
}

WhatsNewModal.propTypes = {
  post: PropTypes.shape({
    id: PropTypes.number,
    slug: PropTypes.string,
    title: PropTypes.string,
    summary: PropTypes.string,
    cover_image_url: PropTypes.string,
  }),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default WhatsNewModal
